import { useState } from 'react';
import { Card, Stack, TableBody, TableContainer } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ContentWrapper } from '@/components/ContentWrapper';
import CardHeader from '@/components/shared/CardHeader';
import { PrimaryButton } from '@/components/shared/Button';
import Chip, { ChipColorVariant } from '@/components/shared/Chip';
import { SMBold, SMRegular } from '@/typography/Typography';
import {
    Table,
    TableHeader,
    TableHeaderCell,
    TableCell,
    TableRow
} from '@/components/shared/styles/Table';
import { useGetHouseAccounts } from '@/services/paymentsQuery';
import {
    HouseAccountBankConnectionStatus,
    InstaPaidHouseAccount
} from '@/services/PaymentsServiceV2';
import CreateHouseAccountDialog from './CreateHouseAccountDialog';

const CONNECTION_STATE_CHIP: Record<
    HouseAccountBankConnectionStatus,
    { color: ChipColorVariant; labelKey: string }
> = {
    Active: { color: 'success', labelKey: 'house_accounts.state_active' },
    Lapsed: { color: 'warning', labelKey: 'house_accounts.state_lapsed' },
    NotConnected: {
        color: 'muted',
        labelKey: 'house_accounts.state_not_connected'
    }
};

const ConnectionStateChip = ({
    status
}: {
    status: HouseAccountBankConnectionStatus;
}) => {
    const { t } = useTranslation();
    const config =
        CONNECTION_STATE_CHIP[status] ?? CONNECTION_STATE_CHIP.NotConnected;
    return <Chip color={config.color} label={t(config.labelKey)} />;
};

export const HouseAccountsPage = () => {
    const { t } = useTranslation();
    const [dialogOpen, setDialogOpen] = useState(false);

    // Management view lists ALL accounts, including retired ones.
    const { data, isLoading, isError, isSuccess } = useGetHouseAccounts(
        true,
        true
    );
    const accounts: InstaPaidHouseAccount[] = data?.data ?? [];

    return (
        <ContentWrapper>
            <>
                <Card>
                    <CardHeader
                        label={t('house_accounts.page_title')}
                        actions={
                            // The confirm gate derives from this list, so it can
                            // only arm once the list is known. Block account
                            // creation until the query has succeeded — otherwise a
                            // pending/errored list would silently skip the gate.
                            <PrimaryButton
                                disabled={!isSuccess}
                                onClick={() => setDialogOpen(true)}
                            >
                                <SMBold textTransform="none">
                                    {t('house_accounts.add_account')}
                                </SMBold>
                            </PrimaryButton>
                        }
                    />
                    <TableContainer>
                        <Table aria-label="house accounts">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>
                                        {t('house_accounts.name')}
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        {t('house_accounts.currency')}
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        {t('house_accounts.connection_state')}
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {accounts.map((account) => (
                                    <TableRow
                                        key={account.instaPaidHouseAccountId}
                                    >
                                        <TableCell>{account.name}</TableCell>
                                        <TableCell>
                                            {account.currency}
                                        </TableCell>
                                        <TableCell>
                                            <ConnectionStateChip
                                                status={
                                                    account.bankConnectionStatus
                                                }
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {(isLoading || isError || accounts.length === 0) && (
                        <Stack alignItems="center" p={3}>
                            <SMRegular>
                                {isLoading
                                    ? t('house_accounts.loading')
                                    : isError
                                    ? t('house_accounts.load_error')
                                    : t('house_accounts.empty')}
                            </SMRegular>
                        </Stack>
                    )}
                </Card>

                <CreateHouseAccountDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    existingAccounts={accounts}
                    listReady={isSuccess}
                />
            </>
        </ContentWrapper>
    );
};

export default HouseAccountsPage;
